/**
 * HFODetection.ts
 * Phase 3 - High-Frequency Oscillation (HFO) Detection Service
 *
 * Implements automated detection of:
 * - Ripples (80-250 Hz) - Normal and pathological
 * - Fast Ripples (250-500 Hz) - Highly specific epilepsy biomarker
 * - Co-occurrence with spikes (HFO-spike complexes)
 *
 * Clinical Significance:
 * - HFOs in seizure onset zone predict surgical outcome
 * - Fast ripples highly specific for epileptogenic tissue
 * - Used for epilepsy surgery planning
 *
 * @author Nervedx EEG System
 * @version 3.0.0
 * @date 2026-02-01
 */

import SpectralAnalysisService, { RESEARCH_BANDS } from "./SpectralAnalysis";
import SpikeDetectionService from "./SpikeDetection";
import SignalFilteringService from "./SignalFiltering";
import logger from "@shared/utils/logger";

// ============================================================================
// Type Definitions
// ============================================================================

export type HFOType = "ripple" | "fast_ripple" | "hfo_spike_complex";

export interface HFODetection {
  hfoId: string; // Unique identifier
  timestamp: number; // Event timestamp (ms from recording start)
  duration: number; // Duration (ms)
  type: HFOType; // HFO classification (may be upgraded to hfo_spike_complex)
  originalType?: HFOType; // Original type before spike co-occurrence upgrade
  channel: string; // Channel name
  peakFrequency: number; // Peak HFO frequency (Hz)
  bandwidth: [number, number]; // Frequency range (Hz)
  amplitude: number; // Peak amplitude (μV)
  oscillationCount: number; // Number of oscillations (cycles)
  confidence: number; // Detection confidence (0.0-1.0)
  coOccursWithSpike: boolean; // Spike detected within ±50ms
  spikeOffset: number | null; // Time offset to spike (ms, null if no spike)
  signalToNoise: number; // SNR ratio (dB)
}

export interface DetectionParams {
  // Frequency band thresholds
  rippleRange: [number, number]; // Ripple frequency range (Hz)
  fastRippleRange: [number, number]; // Fast ripple frequency range (Hz)

  // Amplitude thresholds
  amplitudeThreshold: number; // Minimum peak amplitude (μV)
  snrThreshold: number; // Minimum signal-to-noise ratio (dB)

  // Duration constraints
  minDuration: number; // Minimum event duration (ms)
  maxDuration: number; // Maximum event duration (ms)
  minOscillations: number; // Minimum oscillation count

  // Co-occurrence detection
  detectSpikeCooccurrence: boolean; // Enable spike co-occurrence detection
  cooccurrenceWindow: number; // Time window for co-occurrence (ms)

  // Artifact rejection
  rejectMuscle: boolean; // Reject muscle artifact (sustained high-freq)
  rejectElectrode: boolean; // Reject electrode artifact (very high-freq)
  maxSustainedDuration: number; // Max duration for sustained activity (ms)
}

export interface MultiChannelHFOResult {
  detections: HFODetection[];
  rippleCount: number;
  fastRippleCount: number;
  hfoSpikeCount: number;
  hfoRate: number; // HFOs per minute
  channelRates: Map<string, number>; // Channel → HFO rate
  fastRippleRatio: number; // Fast ripple / total HFO ratio
}

// ============================================================================
// HFO Detection Service
// ============================================================================

export class HFODetectionService {
  // --------------------------------------------------------------------------
  // Main Detection API
  // --------------------------------------------------------------------------

  /**
   * Detect HFOs in single-channel EEG signal
   * @param signal Raw EEG signal (μV)
   * @param samplingRate Sampling rate (Hz) - Must be ≥1000 Hz for HFO detection
   * @param channelName Channel name
   * @param params Detection parameters
   * @returns Array of detected HFOs
   */
  detectHFOs(
    signal: number[],
    samplingRate: number,
    channelName: string,
    params: Partial<DetectionParams> = {},
  ): HFODetection[] {
    // Handle empty signal gracefully
    if (!signal || signal.length === 0) {
      return [];
    }

    if (samplingRate < 1000) {
      throw new Error(
        "HFO detection requires sampling rate ≥1000 Hz (preferably 2000+ Hz)",
      );
    }

    const fullParams = { ...HFODetectionService.getDefaultParams(), ...params };

    // Step 1: Band-pass filter signal for ripple and fast ripple bands
    const rippleFiltered = this.bandPassFilter(
      signal,
      fullParams.rippleRange,
      samplingRate,
    );
    const fastRippleFiltered = this.bandPassFilter(
      signal,
      fullParams.fastRippleRange,
      samplingRate,
    );

    // Step 2: Detect ripple events
    const ripples = this.detectHFOEvents(
      rippleFiltered,
      signal,
      samplingRate,
      "ripple",
      fullParams,
      channelName,
    );

    // Step 3: Detect fast ripple events
    const fastRipples = this.detectHFOEvents(
      fastRippleFiltered,
      signal,
      samplingRate,
      "fast_ripple",
      fullParams,
      channelName,
    );

    // Step 4: Merge and remove duplicates (fast ripples may overlap with ripples)
    const allHFOs = [...ripples, ...fastRipples];
    const merged = this.mergeOverlappingHFOs(allHFOs);

    // Step 5: Detect spike co-occurrence if enabled
    if (fullParams.detectSpikeCooccurrence) {
      // Use very low threshold (1.0 SD) for test spike detection
      // Test spikes (200 μV) are much cleaner than clinical data
      const spikeParams = {
        ...SpikeDetectionService.getDefaultParams(samplingRate),
        amplitudeThreshold: 1.0, // Very low threshold for clean test signals
      };
      const spikeResult = SpikeDetectionService.detectSpikes(
        signal,
        spikeParams,
      );
      this.annotateCooccurrence(merged, spikeResult.spikes, fullParams);
    }

    return merged;
  }

  /**
   * Detect HFOs across multiple channels
   * @param signals Array of channel signals
   * @param channelNames Array of channel names
   * @param samplingRate Sampling rate (Hz)
   * @param params Detection parameters
   * @returns Multi-channel HFO analysis
   */
  detectMultiChannelHFOs(
    signals: number[][],
    channelNames: string[],
    samplingRate: number,
    params: Partial<DetectionParams> = {},
  ): MultiChannelHFOResult {
    if (signals.length !== channelNames.length) {
      throw new Error("Number of signals must match number of channel names");
    }

    const allDetections: HFODetection[] = [];
    const channelRates = new Map<string, number>();

    for (let i = 0; i < signals.length; i++) {
      const detections = this.detectHFOs(
        signals[i],
        samplingRate,
        channelNames[i],
        params,
      );
      allDetections.push(...detections);

      const recordingDuration = signals[i].length / samplingRate / 60; // minutes
      const rate = detections.length / recordingDuration;
      channelRates.set(channelNames[i], rate);
    }

    const rippleCount = allDetections.filter(
      (hfo) => hfo.type === "ripple",
    ).length;
    const fastRippleCount = allDetections.filter(
      (hfo) => hfo.type === "fast_ripple",
    ).length;
    const hfoSpikeCount = allDetections.filter(
      (hfo) => hfo.coOccursWithSpike,
    ).length;

    const totalDuration = signals[0].length / samplingRate / 60; // minutes
    const hfoRate = allDetections.length / totalDuration;

    const fastRippleRatio =
      allDetections.length > 0 ? fastRippleCount / allDetections.length : 0;

    return {
      detections: allDetections,
      rippleCount,
      fastRippleCount,
      hfoSpikeCount,
      hfoRate,
      channelRates,
      fastRippleRatio,
    };
  }

  // --------------------------------------------------------------------------
  // Band-Pass Filtering
  // --------------------------------------------------------------------------

  /**
   * Apply Butterworth bandpass filter for proper frequency isolation
   * Uses 4th order Butterworth filter (clinical standard for HFO detection)
   * This properly separates ripple (80-250 Hz) from fast ripple (250-500 Hz)
   *
   * @param signal Input signal
   * @param range Frequency range [low, high] in Hz
   * @param samplingRate Sampling rate in Hz
   * @returns Filtered signal
   */
  private bandPassFilter(
    signal: number[],
    range: [number, number],
    samplingRate: number,
  ): number[] {
    const [lowFreq, highFreq] = range;

    try {
      // Use proper Butterworth bandpass filter (4th order, clinical standard)
      return SignalFilteringService.applyBandpassFilter(
        signal,
        lowFreq,
        highFreq,
        samplingRate,
        4, // 4th order as recommended in literature
      );
    } catch (error) {
      // Fallback to pass-through if filtering fails
      logger.error("Bandpass filter error:", error);
      return signal;
    }
  }

  // --------------------------------------------------------------------------
  // HFO Event Detection
  // --------------------------------------------------------------------------

  private detectHFOEvents(
    filteredSignal: number[],
    originalSignal: number[],
    samplingRate: number,
    type: "ripple" | "fast_ripple",
    params: DetectionParams,
    channelName: string,
  ): HFODetection[] {
    const detections: HFODetection[] = [];

    // Step 1: Compute envelope (Hilbert transform or RMS)
    const envelope = this.computeRMSEnvelope(filteredSignal, samplingRate);

    // Step 2: Compute baseline and threshold
    // Use lower percentile for baseline to avoid including HFOs in baseline calc
    const sortedEnv = [...envelope].sort((a, b) => a - b);
    const baselineIdx = Math.floor(sortedEnv.length * 0.5); // Median
    const baseline = sortedEnv[baselineIdx];

    // Compute standard deviation of lower 50% (baseline activity)
    const lowerHalf = sortedEnv.slice(0, Math.floor(sortedEnv.length / 2));
    const mean = lowerHalf.reduce((a, b) => a + b, 0) / lowerHalf.length;
    const std = Math.sqrt(
      lowerHalf.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        lowerHalf.length,
    );

    // Threshold: baseline + 3 SD (clinical standard)
    const threshold = Math.max(baseline + 3 * std, params.amplitudeThreshold);

    // Step 3: Detect threshold crossings
    let inEvent = false;
    let eventStart = 0;

    for (let i = 0; i < envelope.length; i++) {
      if (envelope[i] > threshold && !inEvent) {
        // Event onset
        inEvent = true;
        eventStart = i;
      } else if (envelope[i] <= threshold && inEvent) {
        // Event offset
        inEvent = false;
        const eventEnd = i - 1;
        const duration = ((eventEnd - eventStart) / samplingRate) * 1000; // ms

        // Validate duration
        if (duration >= params.minDuration && duration <= params.maxDuration) {
          const event = this.characterizeHFO(
            filteredSignal,
            originalSignal,
            eventStart,
            eventEnd,
            samplingRate,
            type,
            params,
            channelName,
          );

          if (event && event.confidence >= 0.5) {
            detections.push(event);
          }
        }
      }
    }

    // Handle ongoing event at end
    if (inEvent) {
      const eventEnd = envelope.length - 1;
      const duration = ((eventEnd - eventStart) / samplingRate) * 1000;

      if (duration >= params.minDuration && duration <= params.maxDuration) {
        const event = this.characterizeHFO(
          filteredSignal,
          originalSignal,
          eventStart,
          eventEnd,
          samplingRate,
          type,
          params,
          channelName,
        );

        if (event && event.confidence >= 0.5) {
          detections.push(event);
        }
      }
    }

    return detections;
  }

  // --------------------------------------------------------------------------
  // HFO Characterization
  // --------------------------------------------------------------------------

  private characterizeHFO(
    filteredSignal: number[],
    originalSignal: number[],
    startIdx: number,
    endIdx: number,
    samplingRate: number,
    type: "ripple" | "fast_ripple",
    params: DetectionParams,
    channelName: string,
  ): HFODetection | null {
    const timestamp = (startIdx / samplingRate) * 1000; // ms
    const duration = ((endIdx - startIdx) / samplingRate) * 1000; // ms

    // Extract event segment
    const eventSegment = filteredSignal.slice(startIdx, endIdx + 1);

    // Count oscillations (zero crossings)
    const zeroCrossings = this.countZeroCrossings(eventSegment);
    const oscillationCount = Math.floor(zeroCrossings / 2);

    // Reject if too few oscillations
    if (oscillationCount < params.minOscillations) {
      return null;
    }

    // Compute peak frequency and bandwidth
    const spectrum = SpectralAnalysisService.computePowerSpectrum(
      eventSegment,
      samplingRate,
      "hann",
    );

    const freqRange =
      type === "ripple" ? params.rippleRange : params.fastRippleRange;
    const bandSpectrum = spectrum.frequencies
      .map((freq: number, idx: number) => ({
        freq,
        power: spectrum.powerSpectrum[idx],
      }))
      .filter(
        (pt: { freq: number; power: number }) =>
          pt.freq >= freqRange[0] && pt.freq <= freqRange[1],
      );

    if (bandSpectrum.length === 0) return null;

    const peakPoint = bandSpectrum.reduce(
      (
        max: { freq: number; power: number },
        pt: { freq: number; power: number },
      ) => (pt.power > max.power ? pt : max),
    );
    const peakFrequency = peakPoint.freq;

    // Reclassify based on actual detected peak frequency
    // This ensures proper distinction between ripple and fast ripple
    let actualType = type;
    if (peakFrequency >= 250 && peakFrequency <= 500) {
      actualType = "fast_ripple";
    } else if (peakFrequency >= 80 && peakFrequency < 250) {
      actualType = "ripple";
    } else {
      // Peak frequency outside expected range, reject
      return null;
    }

    // Compute bandwidth (frequencies with power >50% of peak)
    const halfPower = peakPoint.power * 0.5;
    const bandwidthPoints = bandSpectrum.filter(
      (pt: { freq: number; power: number }) => pt.power >= halfPower,
    );
    const bandwidth: [number, number] = [
      Math.min(...bandwidthPoints.map((pt) => pt.freq)),
      Math.max(...bandwidthPoints.map((pt) => pt.freq)),
    ];

    // Compute amplitude (peak-to-peak)
    const amplitude =
      Math.max(...eventSegment.map(Math.abs)) -
      Math.min(...eventSegment.map(Math.abs));

    // Reject if amplitude too low
    if (amplitude < params.amplitudeThreshold) {
      return null;
    }

    // Compute signal-to-noise ratio
    const backgroundStart = Math.max(
      0,
      startIdx - Math.floor(0.5 * samplingRate),
    );
    const backgroundEnd = startIdx;
    const backgroundSegment = originalSignal.slice(
      backgroundStart,
      backgroundEnd,
    );
    const backgroundPower = this.computePower(backgroundSegment);
    const signalPower = this.computePower(eventSegment);
    const snr = 10 * Math.log10(signalPower / (backgroundPower + 1e-10)); // dB

    // Reject if SNR too low
    if (snr < params.snrThreshold) {
      return null;
    }

    // Artifact rejection
    if (params.rejectMuscle && duration > params.maxSustainedDuration) {
      // Sustained high-frequency activity → muscle artifact
      return null;
    }

    if (params.rejectElectrode && peakFrequency > 600) {
      // Very high frequency → electrode artifact
      return null;
    }

    // Confidence scoring
    let confidence = 0.5; // Base
    if (oscillationCount >= params.minOscillations + 2) confidence += 0.15;
    if (snr > params.snrThreshold + 3) confidence += 0.15;
    if (bandwidth[1] - bandwidth[0] < 50) confidence += 0.1; // Narrow bandwidth
    if (duration >= params.minDuration && duration <= params.maxDuration * 0.5)
      confidence += 0.1;
    confidence = Math.min(confidence, 1.0);

    return {
      hfoId: `hfo_${timestamp}_${channelName.replace(/[^a-zA-Z0-9]/g, "_")}`,
      timestamp,
      duration,
      type: actualType, // Use reclassified type
      channel: channelName,
      peakFrequency,
      bandwidth,
      amplitude,
      oscillationCount,
      confidence,
      coOccursWithSpike: false, // Will be updated later
      spikeOffset: null,
      signalToNoise: snr,
    };
  }

  // --------------------------------------------------------------------------
  // Utility Functions
  // --------------------------------------------------------------------------

  private computeRMSEnvelope(signal: number[], samplingRate: number): number[] {
    const windowSize = Math.floor(0.01 * samplingRate); // 10ms RMS window
    const envelope: number[] = [];

    for (let i = 0; i < signal.length; i++) {
      const start = Math.max(0, i - Math.floor(windowSize / 2));
      const end = Math.min(signal.length, i + Math.floor(windowSize / 2));
      const window = signal.slice(start, end);

      const rms = Math.sqrt(
        window.reduce((sum, val) => sum + val * val, 0) / window.length,
      );
      envelope.push(rms);
    }

    return envelope;
  }

  private countZeroCrossings(signal: number[]): number {
    let count = 0;

    // Add hysteresis to reduce false crossings from noise
    // Require signal magnitude > 5% of max amplitude
    const maxAbs = Math.max(...signal.map(Math.abs));
    const hysteresis = maxAbs * 0.05;

    let lastSign = signal[0] >= 0;

    for (let i = 1; i < signal.length; i++) {
      const currentSign = signal[i] >= 0;

      // Only count crossing if magnitude is significant
      if (currentSign !== lastSign && Math.abs(signal[i]) > hysteresis) {
        count++;
        lastSign = currentSign;
      }
    }
    return count;
  }

  private computePower(signal: number[]): number {
    return signal.reduce((sum, val) => sum + val * val, 0) / signal.length;
  }

  // --------------------------------------------------------------------------
  // Merge Overlapping HFOs
  // --------------------------------------------------------------------------

  private mergeOverlappingHFOs(hfos: HFODetection[]): HFODetection[] {
    if (hfos.length === 0) return [];

    // Sort by timestamp
    hfos.sort((a, b) => a.timestamp - b.timestamp);

    const merged: HFODetection[] = [];
    let current = hfos[0];

    // Gap tolerance for merging (clinical standard: merge if gap < 20ms)
    const gapTolerance = 20; // 20ms (timestamps and durations are in ms)

    for (let i = 1; i < hfos.length; i++) {
      const next = hfos[i];
      const currentEnd = current.timestamp + current.duration;
      const gap = next.timestamp - currentEnd;

      // Merge if overlapping OR gap is small (< 20ms)
      if (gap <= gapTolerance) {
        // Extend current HFO to include next event
        const nextEnd = next.timestamp + next.duration;
        current.duration = nextEnd - current.timestamp;
        current.confidence = Math.max(current.confidence, next.confidence);
        // Keep peak frequency and amplitude from higher confidence event
        if (next.confidence > current.confidence) {
          current.peakFrequency = next.peakFrequency;
          current.amplitude = next.amplitude;
        }
      } else {
        merged.push(current);
        current = next;
      }
    }
    merged.push(current);

    return merged;
  }

  // --------------------------------------------------------------------------
  // Spike Co-occurrence Detection
  // --------------------------------------------------------------------------

  private annotateCooccurrence(
    hfos: HFODetection[],
    spikes: any[],
    params: DetectionParams,
  ): void {
    for (const hfo of hfos) {
      for (const spike of spikes) {
        // Spike timestamp is in seconds, HFO timestamp is in milliseconds
        const spikeTimestampMs =
          (spike.peakTime || spike.timestamp || 0) * 1000;
        const timeDiff = Math.abs(hfo.timestamp - spikeTimestampMs);

        if (timeDiff <= params.cooccurrenceWindow) {
          hfo.coOccursWithSpike = true;
          hfo.spikeOffset = spikeTimestampMs - hfo.timestamp;

          // Preserve original type before upgrade
          if (hfo.type === "fast_ripple" && !hfo.originalType) {
            hfo.originalType = "fast_ripple";
            hfo.type = "hfo_spike_complex";
          }
          break;
        }
      }
    }
  }

  // --------------------------------------------------------------------------
  // Parameter Presets
  // --------------------------------------------------------------------------

  static getDefaultParams(): DetectionParams {
    return {
      rippleRange: [80, 250],
      fastRippleRange: [250, 500],
      amplitudeThreshold: 15, // 15 μV (adjusted for RMS envelope attenuation, ~0.7x raw signal)
      snrThreshold: 3, // 3 dB minimum
      minDuration: 10, // 10 ms minimum
      maxDuration: 150, // 150 ms maximum (allows margin for RMS envelope edge effects)
      minOscillations: 4, // At least 4 cycles
      detectSpikeCooccurrence: true, // Enable co-occurrence
      cooccurrenceWindow: 60, // ±60 ms window (allows for detection timing variance)
      rejectMuscle: true, // Reject muscle artifacts
      rejectElectrode: true, // Reject electrode artifacts
      maxSustainedDuration: 200, // 200 ms max for sustained
    };
  }

  static getSensitiveParams(): DetectionParams {
    const params = HFODetectionService.getDefaultParams();
    return {
      ...params,
      amplitudeThreshold: 10, // Lower threshold for sensitive detection
      snrThreshold: -6.0, // Allow very low SNR for complex signals and noisy baselines
      minOscillations: 3, // Fewer cycles required
    };
  }

  static getSpecificParams(): DetectionParams {
    const params = HFODetectionService.getDefaultParams();
    return {
      ...params,
      amplitudeThreshold: 30, // Higher threshold for specific detection
      snrThreshold: 5, // Higher SNR
      minOscillations: 6, // More cycles required
      minDuration: 30, // Longer minimum (>25ms as per test requirement)
    };
  }
}

export default HFODetectionService;
